/**
 * THE write path for a startup's own company profile.
 *
 * Companion to `use-save-personal-profile.ts`, for the same reason: before this,
 * `src/app/edit-company.tsx` carried an explicit `// Simulate save delay`,
 * awaited `setTimeout(resolve, 800)`, wrote to the persisted Zustand store and
 * reported success. It never imported supabase. Every company edit — name,
 * tagline, traction, market, problem/solution — was lost to every other device
 * and to the server, and vanished at sign-out.
 *
 * AUTHORIZATION IS RLS, NOT THIS FILE
 *   `startup_profiles` is updatable by an ACTIVE team member, via the policy
 *   "Startup profiles: team members can update"
 *   (`is_active_startup_team_member(id, NULL)`), which is the sanctioned
 *   ownership path (see the OWNERSHIP INVARIANT in CLAUDE.md). This hook does
 *   not check ownership itself and must not: the database is the authority, and
 *   a client-side check would be both redundant and bypassable.
 *
 *   Verified against the LIVE catalogue rather than the migration files. Note
 *   two further UPDATE policies still read `startup_profiles.user_id`, the
 *   retired column — they are dead (it is empty), so team membership is the
 *   only path that can actually grant.
 *
 * COLUMN NAMES ARE THE DATABASE'S, NOT THE FORM'S
 *   The screen speaks camelCase; the table speaks snake_case. The mapping lives
 *   here, once, so no screen has to know it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * What a founder may change about their company.
 *
 * DELIBERATELY ABSENT — these have NO column on `startup_profiles`, so a screen
 * that collects them cannot save them and must not pretend to:
 *   - minimumTicket
 *   - syndicatesAllowed
 * Both are fundraising-round concepts; `fundraising_rounds` is the table that
 * models a round. Rehoming them is a separate change.
 */
export interface CompanyProfileEdit {
  name: string;
  tagline?: string;
  industry?: string;
  stage?: string;
  location?: string;
  website?: string;
  linkedIn?: string;
  teamSize?: number;
  foundedYear?: number;
  fundingTarget?: number;
  isActivelyRaising?: boolean;
  problem?: string;
  solution?: string;
  targetCustomer?: string;
  monthlyRevenue?: number;
  userCount?: number;
  growthRate?: number;
  marketSize?: number;
  competitors?: string[];
  isSerialFounder?: boolean;
  previousVentures?: string[];
}

/** Empty string → null, so a cleared field clears the column instead of storing ''. */
const text = (v: string | undefined) => {
  const t = v?.trim();
  return t ? t : null;
};
/** NaN/0-from-parse → null rather than writing a bogus zero. */
const num = (v: number | undefined) =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/**
 * Persist the company profile. Server first, error surfaced, cache invalidated.
 */
export function useSaveCompanyProfile(startupId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (edit: CompanyProfileEdit) => {
      if (!startupId) {
        throw new Error(
          'No company is linked to your account yet, so there is nothing to save.',
        );
      }

      const { error } = await supabase
        .from('startup_profiles')
        .update({
          name: edit.name.trim(),
          tagline: text(edit.tagline),
          industry: text(edit.industry),
          // `stage` and `raising_status` are enums; an empty value must be left
          // alone rather than written as null, which would fail the cast.
          ...(edit.stage ? { stage: edit.stage } : {}),
          location: text(edit.location),
          website: text(edit.website),
          linkedin: text(edit.linkedIn),
          team_size: num(edit.teamSize),
          founded_year: num(edit.foundedYear),
          funding_target: num(edit.fundingTarget),
          is_actively_raising: edit.isActivelyRaising ?? false,
          problem: text(edit.problem),
          solution: text(edit.solution),
          target_customer: text(edit.targetCustomer),
          monthly_revenue: num(edit.monthlyRevenue),
          user_count: num(edit.userCount),
          growth_rate: num(edit.growthRate),
          market_size: num(edit.marketSize),
          competitors: edit.competitors?.length ? edit.competitors : null,
          is_serial_founder: edit.isSerialFounder ?? false,
          previous_ventures: edit.previousVentures?.length ? edit.previousVentures : null,
        } as never)
        .eq('id', startupId);

      // supabase-js resolves with { error } rather than throwing, so an
      // unchecked result is a silent failure (ARCH-06). RLS denial arrives here
      // too: a non-member gets zero rows updated and no error, which is why the
      // caller re-reads rather than trusting local state.
      if (error) throw error;
    },

    onSuccess: () => {
      // Everything keyed on the startup must re-read from the server rather than
      // trust an optimistic local copy — that assumption is what this file exists
      // to remove.
      queryClient.invalidateQueries({ queryKey: ['my-startup'] });
      queryClient.invalidateQueries({ queryKey: ['startup-profile', startupId] });
      queryClient.invalidateQueries({ queryKey: ['startup-dashboard-stats'] });
    },
  });
}
